import { MBIconV2Name } from '@shared/components/image';
import { ReferencedInComponent, RelatedCardComponent } from '@shared/scenes/workflows/config';
import { WorkspaceDetail, useAllViewTemplates } from '@shared/scenes/workflows/hooks';
import { isRelatedCardComponent } from '@shared/scenes/workflows/utils';
import { useSetComponentField } from '../../../hooks';
import { LinkableWorkflow } from '../relatedCardOptions/RelatedCardLinkingOptions';
import { ListItem } from './SelectableItemList';
import { useReferenceableWorkflows } from './useReferenceableWorkflows';

interface ReferencedInOptions {
  selectedWorkflowTemplate?: LinkableWorkflow;
  setSelectedWorkflowId: (selectedWorkflowId?: string) => void;
  selectedComponent?: RelatedCardComponent;
  setSelectedComponentId: (selectedComponentId?: string) => void;
  selectedViewTemplate?: ListItem;
  setSelectedViewTemplate: (selectedViewTemplateId?: string) => void;
  selectableWorkflowItems: ListItem[];
  selectableComponentItems: ListItem[];
  selectableViewTemplates: ListItem[];
  workflowLoading: boolean;
  viewTemplatesLoading: boolean;
}

export function useReferencedInOptions(
  workflowTemplateId: string,
  workspaceDetails: Map<string, WorkspaceDetail>,
  component: ReferencedInComponent,
): ReferencedInOptions {
  const [selectedWorkflowId, setSelectedWorkflow] = useSetComponentField<
    string | undefined,
    ReferencedInComponent
  >(component, 'referencedFromWorkflowTemplateId');
  const [selectedComponentId, setSelectedComponent] = useSetComponentField<
    string | undefined,
    ReferencedInComponent
  >(component, 'relatedCardComponentId');
  const [selectedViewTemplateId, setSelectedViewTemplate] = useSetComponentField<
    string | undefined,
    ReferencedInComponent
  >(component, 'referencingViewTemplateId');
  const referenceableWorkflows = useReferenceableWorkflows(workflowTemplateId, workspaceDetails);
  const viewTemplates = useAllViewTemplates(selectedWorkflowId, 'COLLECTION', 'LIST');
  const selectedWorkflowTemplate = referenceableWorkflows.workflows?.find(
    workflow => workflow.workflowTemplateId === selectedWorkflowId,
  );
  const selectableComponents: RelatedCardComponent[] =
    selectedWorkflowTemplate?.components
      .filter(isRelatedCardComponent)
      .filter(item => item.workflowTemplateId === workflowTemplateId) ?? [];

  const selectedComponent = selectableComponents.find(item => item.id === selectedComponentId);

  const selectableWorkflowItems: ListItem[] = referenceableWorkflows.workflows.map(workflow => {
    return {
      id: workflow.workflowTemplateId,
      label: workspaceDetails.get(workflow.workflowTemplateId)?.name ?? '',
      imageUrl: workflow.imageUrl,
    };
  });

  const selectableComponentItems: ListItem[] = selectableComponents?.map(item => {
    return { id: item.id, label: item.label ?? '', icon: item.icon as MBIconV2Name };
  });

  const selectableViewTemplates: ListItem[] = viewTemplates?.viewTemplates
    ?.filter(viewTemplate => viewTemplate.subtype === 'LIST')
    ?.map(viewTemplate => {
      return { id: viewTemplate.id, label: viewTemplate.name };
    });

  const selectedViewTemplate = selectableViewTemplates.find(vt => vt.id === selectedViewTemplateId);

  return {
    selectedWorkflowTemplate,
    setSelectedWorkflowId: setSelectedWorkflow,
    selectedComponent,
    setSelectedComponentId: setSelectedComponent,
    selectedViewTemplate,
    setSelectedViewTemplate,
    selectableWorkflowItems,
    selectableComponentItems,
    selectableViewTemplates,
    workflowLoading: referenceableWorkflows.loading,
    viewTemplatesLoading: viewTemplates.loading,
  };
}
